import '/assets/styles/partials/qr-reader.scss';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

// Partials
import Btn from '/views/partials/btn';
import Loader from '/views/partials/loader';

// Context
import { useApplication } from '/utilities/application';
import { useModals } from '@wearetla/tla-essentials-tools/utilities/modals';
import { useTranslation } from '/utilities/i18n';

const readerId = 'qr-code-reader';

const QrReader = ({ config: rawConfig = {}, onSuccess, onError, className, paused }) => {
	const { t } = useTranslation();
	const { isApplication, postMessage, cameraAllowed: appCameraAllowed } = useApplication();
	const [allowCamera, setAllowCamera] = useState(!isApplication);
	const [torchButtonAvailable, setTorchFeatureSupported] = useState(false);
	const [cameraReady, setCameraReady] = useState(false);
	const [torchEnabled, setTorchEnabled] = useState(false);
	const blockTimeout = useRef(null);
	const torchFeatureRef = useRef(null);
	const { openModal } = useModals();

	// Keep the latest callbacks/config in refs so the scanner can be started
	// exactly once (on mount) while always invoking the current handlers.
	const onSuccessRef = useRef(onSuccess);
	const onErrorRef = useRef(onError);
	const configRef = useRef(rawConfig);
	onSuccessRef.current = onSuccess;
	onErrorRef.current = onError;
	configRef.current = rawConfig;

	const requestCameraPermission = () => {
		postMessage('requestCameraPermission');
	};

	const toggleTorch = () => {
		setTorchEnabled(!torchEnabled);
	};

	useEffect(() => {
		try {
			torchFeatureRef.current?.apply(torchEnabled);
		} catch (e) {
			console.error('Torch error:', e);
			setTorchEnabled(false);
		}
	}, [torchEnabled]);

	useEffect(() => {
		if (appCameraAllowed) {
			setAllowCamera(true);
		}
	}, [appCameraAllowed]);

	useEffect(() => {
		requestCameraPermission();

		return () => {
			try {
				if (window.qrReaderInstance) {
					window.qrReaderInstance
						?.stop?.()
						.then(() => {
						})
						.catch((error) => {
							console.error('Failed to clear Html5Qrcode. ', error);
						});
					window.qrReaderInstance = null;
				}
				if (blockTimeout.current) {
					clearTimeout(blockTimeout.current);
				}
				window.blockQrRead = false;
			} catch (e) {
				console.error('QR Reader error, ignore');
			}
		};
	}, []);

	useEffect(() => {
		if (!window.qrReaderInstance) {
			try {
				const { verbose = false, ...configOptions } = configRef.current;
				const config = {
					// Higher fps than the previous `1` so the scanner locks onto a
					// code quickly; kept cheap by the focused qrbox + disableFlip below.
					fps: 10,
					aspectRatio: 0.55,
					// Skip the mirrored-image decode pass — physical codes are never
					// flipped, so this halves the per-frame decode work.
					disableFlip: true,
					// Only decode the central region the crosshair points at instead of
					// the whole frame — the biggest CPU saving and it also improves
					// detection accuracy.
					qrbox: (viewfinderWidth, viewfinderHeight) => {
						const size = Math.floor(Math.min(viewfinderWidth, viewfinderHeight) * 0.7);
						return { width: size, height: size };
					},
					...configOptions,
				};

				if (!onSuccessRef.current) {
					throw 'onSuccess is required callback.';
				}

				window.qrReaderInstance = new Html5Qrcode(readerId, {
					verbose: !!verbose,
					formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
				});

				const successCallback = (decodedText, decodedResult) => {
					if (!window.blockQrRead) {
						window.blockQrRead = true;
						onSuccessRef.current?.(decodedText, decodedResult);

						blockTimeout.current = setTimeout(() => {
							window.blockQrRead = false;
						}, 2000);
					}
				};

				const errorCallback = (...args) => onErrorRef.current?.(...args);

				window.qrReaderInstance
					.start({ facingMode: 'environment' }, config, successCallback, errorCallback)
					.then(() => {
						setCameraReady(true);

						const cameraCapabilities = window.qrReaderInstance.getRunningTrackCameraCapabilities();
						torchFeatureRef.current = cameraCapabilities?.torchFeature();
						setTorchFeatureSupported(torchFeatureRef.current?.isSupported());

						// Ask the camera to focus continuously. Without this html5-qrcode
						// leaves the lens on its slow default one-shot focus, which is the
						// usual cause of "takes several seconds to focus". Guarded by a
						// capability check — iOS Safari / some Android cams don't expose it.
						try {
							const track = window.qrReaderInstance.getRunningTrack?.();
							const capabilities = track?.getCapabilities?.();
							if (capabilities?.focusMode?.includes('continuous')) {
								track
									.applyConstraints({ advanced: [{ focusMode: 'continuous' }] })
									.catch((focusError) => {
										console.warn('Continuous autofocus not applied:', focusError);
									});
							}
						} catch (focusError) {
							console.warn('Continuous autofocus not supported:', focusError);
						}
					})
					.catch((e) => {
						setCameraReady(true);
						openModal('message', { feedback: t('QR okuyucu başlatılamadı, lütfen tekrar deneyin.') });
						console.error('QR Error', e);
					});
			} catch (e) {
				setCameraReady(true);
				openModal('message', { feedback: t('QR okuyucu başlatılamadı, lütfen tekrar deneyin.') });
				console.error('QR Error', e);
			}
		}
		// Runs once on mount. Callbacks/config are read through refs so the camera
		// is started a single time while always invoking the latest handlers.
	}, []);

	useEffect(() => {
		try {
			if (window.qrReaderInstance) {
				const instancePaused = window.qrReaderInstance.getState?.() === 3;
				if (paused && !instancePaused) {
					window.qrReaderInstance.pause?.();
				} else if (!paused && instancePaused) {
					window.qrReaderInstance.resume?.();
				}
			}
		} catch (e) {
			console.error('QR Reader error, ignore');
		}
	}, [paused]);

	return (
		<div className={classNames('qr-reader', className)}>
			<div className="qr-reader-video" id={readerId} />
			<Loader
				rich
				loading={allowCamera && !cameraReady}
				className="reader-loader"
				message={t('Kamera hazırlanıyor...')}
			/>
			<div className="qr-reader-overlay">
				<div className="overlay-crosshair" />
				<div className="overlay-message">{t('QR Kodu Okutun')}</div>
			</div>
			{!allowCamera && !appCameraAllowed && (
				<Btn onClick={requestCameraPermission} className="reader-camerabtn primary">
					{t('Kamera İzni İste')}
				</Btn>
			)}
			{allowCamera && torchButtonAvailable && (
				<Btn
					onClick={toggleTorch}
					className={classNames('reader-torchbtn', 'big', {
						white: !torchEnabled,
						primary: torchEnabled,
					})}
					icon={torchEnabled ? 'torch-on' : 'torch-off'}
				/>
			)}
		</div>
	);
};

export default QrReader;
